import type {
  CompletedPnLSummary,
  FetchedPnL,
  PnLSourceQuality,
} from '../types/strategy';

export type PnlSourceLabelKey =
  | 'verifiedFromBinanceApi'
  | 'waitingForBinanceSync'
  | 'binanceHistoryIncomplete'
  | 'unverified';

export interface PnlSourceView {
  quality: PnLSourceQuality;
  labelKey: PnlSourceLabelKey;
  isVerified: boolean;
  hedgePnl: number;
  realizedPnl: number;
  fundingFees: number;
  commissions: number;
  expectedQuantity: number;
  coveredQuantity: number;
  incompleteOrderCount: number;
}

export interface CompletedPnlTotalsByQuality {
  verified: number;
  pending: number;
  incomplete: number;
  unverified: number;
}

export function normalizePnlSourceQuality(
  quality: PnLSourceQuality | null | undefined,
): PnLSourceQuality {
  return quality ?? 'Unverified';
}

export function pnlSourceLabelKey(
  quality: PnLSourceQuality | null | undefined,
): PnlSourceLabelKey {
  switch (normalizePnlSourceQuality(quality)) {
    case 'FetchedVerified':
      return 'verifiedFromBinanceApi';
    case 'PendingExchangeSync':
      return 'waitingForBinanceSync';
    case 'BinanceArchiveIncomplete':
      return 'binanceHistoryIncomplete';
    case 'Unverified':
      return 'unverified';
  }
}

export function buildPnlSourceView(
  fetched: FetchedPnL | null | undefined,
  localHedgePnl: number,
  localRealizedPnl = localHedgePnl,
): PnlSourceView {
  const quality = normalizePnlSourceQuality(fetched?.source_quality);
  const isVerified = quality === 'FetchedVerified';

  return {
    quality,
    labelKey: pnlSourceLabelKey(quality),
    isVerified,
    hedgePnl: isVerified ? (fetched?.hedge_pnl ?? 0) : localHedgePnl,
    realizedPnl: isVerified
      ? (fetched?.realized_pnl_sum ?? 0)
      : localRealizedPnl,
    fundingFees: fetched?.income_sum ?? 0,
    commissions: fetched?.commission_sum ?? 0,
    expectedQuantity: fetched?.expected_executed_quantity ?? 0,
    coveredQuantity: fetched?.covered_executed_quantity ?? 0,
    incompleteOrderCount: fetched?.incomplete_order_ids?.length ?? 0,
  };
}

export function summarizeCompletedPnlByQuality(
  summaries: CompletedPnLSummary[],
): CompletedPnlTotalsByQuality {
  return summaries.reduce<CompletedPnlTotalsByQuality>(
    (totals, summary) => {
      switch (normalizePnlSourceQuality(summary.source_quality)) {
        case 'FetchedVerified':
          totals.verified += summary.total_pnl_usdt;
          break;
        case 'PendingExchangeSync':
          totals.pending += summary.total_pnl_usdt;
          break;
        case 'BinanceArchiveIncomplete':
          totals.incomplete += summary.total_pnl_usdt;
          break;
        case 'Unverified':
          totals.unverified += summary.total_pnl_usdt;
          break;
      }
      return totals;
    },
    { verified: 0, pending: 0, incomplete: 0, unverified: 0 },
  );
}
